/* The automation designer's save gate and the standard syntax (formula gap-closure §11.4a).
   api/canvastest/automationnative answers a graph that would need the Python engine with the
   standard spellings of the expressions that keep it off the native lane, each located by a path
   from the blocks object, and with compatibleWithRewrites: whether the graph classifies native with
   every one of them in place. The gate offers them, before its refusal, only in that case. */

/* Put each rewrite's text at its path in `blocks`, where the expression still reads as it did when
   classified. Returns how many were put in place. */
export function applyBlockRewrites(blocks, rewrites) {
  let applied = 0;
  (rewrites || []).forEach((rewrite) => {
    let owner = blocks;
    for (const step of rewrite.path || []) {
      if (owner == null) break;
      owner = owner[step];
    }
    if (owner && owner[rewrite.slot] === rewrite.from) {
      owner[rewrite.slot] = rewrite.to;
      applied++;
    }
  });
  return applied;
}

/* The question the gate asks: the first few rewrites as "from → to", and why a checked one may
   answer differently on some entries. */
export function describeBlockRewrites(rewrites, shown = 3) {
  const list = rewrites || [];
  const lines = list.slice(0, shown).map((r) => `${r.from.trim()}  →  ${r.to}`);
  const more = list.length > shown ? ` …and ${list.length - shown} more.` : "";
  const checks = [...new Set(list.filter((r) => r.kind === "checked").flatMap((r) => r.checks || []))];
  const check = checks.length ? ` Check they give the same answers: ${checks.join(" ")}` : "";
  const count = list.length === 1 ? "1 expression needs" : `${list.length} expressions need`;
  return `${count} the Python engine. In the standard syntax, which runs natively: ${lines.join(" • ")}.${more}${check} Replace and save?`;
}

/* Offer the verdict's rewrites; on yes put them in place in `blocks` (the graph being saved).
   True when they were put in place, so the gate can save as native. Any rewrite put in place
   signals refreshAutomationBlocks, so an open designer re-checks its badge and outlines; nothing
   else changes the graph, so without it they keep the verdict of the legacy text. */
export async function offerBlockRewrites(vm, verdict, blocks) {
  if (!verdict || verdict.compatibleWithRewrites !== true || !(verdict.rewrites || []).length) return false;
  const res = await vm.$confirmation.show({
    title: "Use the standard syntax?",
    text: describeBlockRewrites(verdict.rewrites),
  });
  if (res !== true) return false;
  const applied = applyBlockRewrites(blocks, verdict.rewrites);
  if (applied > 0 && window.eventBus) window.eventBus.$emit("refreshAutomationBlocks");
  return applied === verdict.rewrites.length;
}
